import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import PageLayout from '@/components/Layout/PageLayout';
import ExportDataDropDown from '@/components/molecules/ExportDataDropDown';
import DownloadOptions from '@/components/molecules/ExportDataDropDown/DownloadOptions';
import { usePermissions } from '@/contexts/auth';
import { useCage, useCageName } from '@/contexts/cage';
import { cageScopedSearch, DEFAULT_DAYPART_TAG } from '@/contexts/cage-filter-context';
import { OVERVIEW_WEIGHT_UNIT_TAG } from '@/domain/cages';
import { useIsSmolt } from '@/hooks/useCageTypeFlags';
import { useCageOverview } from '@/routes/Client/Cage/components/cage-overview-context';
import type { CagePageInterface } from '@/routes/Client/Cage/utils/cage-pages-interface';
import filterGraphDayparts from '@/routes/Client/Cage/utils/filter-graph-dayparts';
import { formatMachineNumber } from '@/utils/localeFormatters';
import { createCageUrl } from '@/utils/urlGenerator';

import CageWeightDashboardView from './CageWeightDashboardView';

const LIVING_WEIGHT_ALL_TAGS_FLAG = 'LIVING_WEIGHT_ALL_TAGS_ENABLED';
const EXPORT_DECIMALS = 2;

/** Prefix columns the export endpoint expects, in this exact notation. */
const LOCATION_INFO_COLUMNS = [
    'location.name',
    'location.timezone',
    'location.sWeightPct',
    'cage.name',
];

export default function CageWeightDashboardContainer(props: CagePageInterface) {
    const { clientId, locationId, cageId } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { search } = useLocation();
    const { client } = useCage();
    const cageName = useCageName();
    const isSmolt = useIsSmolt();
    const { isOptoScaleAdmin } = usePermissions();

    const {
        data,
        daypartTag,
        loading,
        error,
        isFetching,
        refetch,
        dateFrom,
        dateTo,
        onDateChange,
    } = useCageOverview();

    /**
     * `LIVING_WEIGHT_ALL_TAGS_ENABLED` opts a client into seeing every daypart
     * series rather than only the default one. Set it on the client's feature
     * flags in the database to enable it.
     */
    const hasLivingWeightAllTagsEnabled =
        client?.featureFlags?.includes(LIVING_WEIGHT_ALL_TAGS_FLAG) ?? false;

    const graphedDaypartTags = useMemo(
        () => filterGraphDayparts(isSmolt, isOptoScaleAdmin, hasLivingWeightAllTagsEnabled),
        [isSmolt, isOptoScaleAdmin, hasLivingWeightAllTagsEnabled]
    );

    // Only the requested daypart starts visible. The overview context resolves it
    // (`resolveWeightDaypartTag`): smolt cages always use `all`, since they have no
    // day/night split, so the chart and the cards read the same estimate.
    const resolvedDaypartTag = daypartTag ?? DEFAULT_DAYPART_TAG;
    const activeDaypartTags = useMemo(() => [resolvedDaypartTag], [resolvedDaypartTag]);

    const exportRows = useMemo(
        () =>
            data.biomass.days
                .filter((day) => day.daypartTag === resolvedDaypartTag)
                .map((day) => ({
                    date: day.day,
                    daypartTag: day.daypartTag,
                    measurementsCount: day.data.measCnt,
                    weightAvg: formatMachineNumber(day.data.weightAvg, EXPORT_DECIMALS),
                    weightTrend: formatMachineNumber(day.data.weightTrend, EXPORT_DECIMALS),
                    weightGrowthAvg: formatMachineNumber(day.data.weightGrowthAvg, EXPORT_DECIMALS),
                    weightGrowthTrend: formatMachineNumber(
                        day.data.weightGrowthTrend,
                        EXPORT_DECIMALS
                    ),
                })),
        [data.biomass.days, resolvedDaypartTag]
    );

    // Day columns are namespaced and derived from the first row, so they stay in
    // step with `exportRows` without a second hardcoded list.
    const exportColumns = useMemo(() => {
        const dayColumns = exportRows.length > 0 ? Object.keys(exportRows[0]) : [];
        return [...LOCATION_INFO_COLUMNS, ...dayColumns.map((column) => `day.${column}`)];
    }, [exportRows]);

    return (
        <PageLayout
            title={cageName}
            subtitle={t('common.labels.weight')}
            actions={
                <ExportDataDropDown>
                    <DownloadOptions data={exportRows} columns={exportColumns} />
                </ExportDataDropDown>
            }>
            <CageWeightDashboardView
                days={data.biomass.days}
                graphedDaypartTags={graphedDaypartTags}
                activeDaypartTags={activeDaypartTags}
                daypartTag={resolvedDaypartTag}
                weightUnitTag={OVERVIEW_WEIGHT_UNIT_TAG}
                estimatedWeight={data.biomass.estimate.estimatedWeight}
                period={data.biomass.period}
                isLoading={loading}
                error={error}
                onRetry={refetch}
                isRetrying={isFetching}
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateChange={onDateChange}
                onOpenGraphs={() =>
                    // Carries the cohort, not the range: the graphs page should open
                    // on the same fish group, but on its own general window rather
                    // than on whatever month was picked here.
                    navigate(
                        `${createCageUrl(clientId, locationId, cageId)}/weight/graphs${cageScopedSearch(search)}`
                    )
                }
            />
        </PageLayout>
    );
}
